import * as Sentry from '@sentry/react'

/**
 * Hash a string using SHA-256 for PII protection. The same input always
 * produces the same hash, enabling user correlation across analytics providers
 * without exposing actual PII. Returns '' when hashing is unavailable.
 */
export async function hashPII(value: string): Promise<string> {
  if (!value) return ''

  // SubtleCrypto is unavailable in non-secure contexts, although the DOM
  // typings declare it always present.
  const subtle: SubtleCrypto | undefined = typeof crypto === 'undefined' ? undefined : crypto.subtle
  if (!subtle) return ''

  try {
    const normalized = value.toLowerCase().trim()
    const data = new TextEncoder().encode(normalized)
    const hashBuffer = await subtle.digest('SHA-256', data)
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch (error) {
    Sentry.captureException(error, { tags: { track_function: 'hashPII' } })
    return ''
  }
}
